import type { ReactNode } from 'react';

/**
 * Status badge (UI_GUIDELINES §5 Badge). `tone` keys come from
 * lib/competitions.ts (COMPETITION_STATUS_TONES) and lib/submissions.ts
 * (SUBMISSION_STATUS_TONES), plus a few ad-hoc keys used by pages.
 * Status is carried by the dot colour and the label — never by the dot alone.
 */
interface ToneStyle {
  /** 6 px status dot. `null` = no dot (non-status tags such as patronage). */
  dot: string | null;
  /** Overrides for the badge surface; default is the neutral chip. */
  badge?: string;
}

const DEFAULT_BADGE = 'border-line-strong bg-surface-2 text-fg-secondary';

const tones: Record<string, ToneStyle> = {
  // Competitions
  Draft: { dot: 'bg-fg-muted' },
  Scheduled: { dot: 'bg-info' },
  Active: { dot: 'bg-success' },
  Judging: { dot: 'bg-accent', badge: 'border-accent-ring bg-accent-subtle text-accent-text' },
  Awarding: { dot: 'bg-accent', badge: 'border-accent-ring bg-accent-subtle text-accent-text' },
  Completed: { dot: 'bg-fg' },
  Archived: { dot: 'bg-fg-muted' },
  // Submissions
  Submitted: { dot: 'bg-info' },
  Accepted: { dot: 'bg-success' },
  Rejected: { dot: 'bg-danger', badge: 'border-danger/40 bg-danger-subtle text-danger' },
  // Ad-hoc keys used by pages
  UnderReview: { dot: 'bg-warning' },
  Scored: { dot: 'bg-fg' },
  Fzs: { dot: null },
  /** Picked for the exhibition. */
  Selected: { dot: 'bg-accent', badge: 'border-accent-ring bg-accent-subtle text-accent-text' },
};

export function Badge({ children, tone }: { children: ReactNode; tone?: string }) {
  const style = (tone && tones[tone]) || tones.Draft;
  return (
    <span
      className={`inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 font-mono text-[11px] uppercase tracking-[0.04em] ${style.badge ?? DEFAULT_BADGE}`}
    >
      {style.dot && <span aria-hidden="true" className={`h-1.5 w-1.5 shrink-0 rounded-full ${style.dot}`} />}
      {children}
    </span>
  );
}
